import { ArticleType, AttachmentType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { Box, Divider, Typography } from '@mui/material';
import { Dispatch, Fragment, SetStateAction, useEffect, useMemo, useState } from 'react';
import ExtractedImagesFromFile from './ExtractedImagesFromFile';
import { downloadFilesWithUrls } from '@buildwise/libs/helpers';
import RenderLLMMarkdown from '@buildwise/pages/buildwiseAdmin/BuildWiseGPT/RenderLLMMarkdown';
import { Link } from 'react-router-dom';
import { VIEW_ARTICLE } from 'constants/routes';

type RenderParsedDraftsProps = {
  response: any;
  downloadedFiles: AttachmentType[];
  setDownloadedFiles: Dispatch<SetStateAction<AttachmentType[]>>;
};

function RenderParsedDrafts({
  response,
  downloadedFiles,
  setDownloadedFiles,
}: RenderParsedDraftsProps) {
  const { articles, extractedAttachmentsMetaData } = useMemo(
    () => ({
      articles: (response?.metadata?.articlesList || []) as ArticleType[],
      extractedAttachmentsMetaData: response?.metadata?.documentMetadata,
    }),
    [response],
  );
  const [isDownloadingFiles, setDownloadingFiles] = useState(false);

  useEffect(() => {
    if (extractedAttachmentsMetaData?.imagesList) {
      const downloadFiles = async () => {
        try {
          setDownloadingFiles(true);
          const files = await downloadFilesWithUrls({
            urls: (extractedAttachmentsMetaData?.imagesList || []).map((file) => ({
              url: file.fileURL,
            })),
          });

          setDownloadedFiles(files);
        } catch (error) {
          console.log('DOWNLOAD_FILES_ERROR: ' + error);
        } finally {
          setDownloadingFiles(false);
        }
      };

      downloadFiles();
    }
  }, [extractedAttachmentsMetaData?.imagesList]);

  return (
    <>
      {articles.map((article, index) => (
        <Box
          key={article.id}
          component={Link}
          to={VIEW_ARTICLE.replace(':articleId', article.id)}
          target="_blank"
          sx={{ cursor: 'pointer' }}
        >
          <Typography
            variant="h3"
            sx={(theme) => ({
              fontSize: 18,
              fontWeight: 700,
              color: theme.palette.custom.tuna,
              margin: '12px 0 4px',
            })}
          >
            {article.title}
          </Typography>
          <RenderLLMMarkdown llmResponse={article.description} />
          <ExtractedImagesFromFile
            downloadedFiles={downloadedFiles}
            isDownloadingFiles={isDownloadingFiles}
          />
          {index !== articles.length - 1 && <Divider />}
        </Box>
      ))}
    </>
  );
}

export default RenderParsedDrafts;
